import React, { useEffect, useState, useMemo } from 'react';
import RowActions from '../../components/admin/RowActions';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, Paper, Chip, Skeleton, TableContainer,
  TablePagination, Button, Dialog, DialogTitle, DialogContent,
  Grid, Divider, IconButton, Card, CardContent, Avatar, TextField, InputAdornment,
  DialogActions, CircularProgress, Stack, MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../api';
import { adminHasPermission } from '../../utils/adminPermissions';

const gradientButtonStyle = {
  background: 'linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)',
  color: '#fff',
  fontWeight: 600,
  paddingX: 2,
  paddingY: 0.5,
  borderRadius: 2,
  textTransform: 'none',
  '&:hover': {
    background: 'linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)',
    opacity: 0.9,
  }
};

/** Scroll both axes inside this box so the horizontal bar stays at the bottom of the viewport, not under all rows. */
const courseTableScrollSx = {
  width: '100%',
  maxHeight: 'min(68vh, calc(100vh - 240px))',
  overflow: 'auto',
};

/** One entry per billing period (months) from `prices` on course show API. */
function buildPricingPlanGroups(prices) {
  if (!prices || typeof prices !== 'object') return [];
  const periodIds = new Set();
  Object.keys(prices).forEach((k) => {
    const m = k.match(/amount_for_(?:online|in_person)_(\d+)/);
    if (m) periodIds.add(parseInt(m[1], 10));
  });
  return Array.from(periodIds)
    .sort((a, b) => a - b)
    .map((periodId) => {
      const oAmt = prices[`amount_for_online_${periodId}`];
      const iAmt = prices[`amount_for_in_person_${periodId}`];
      const planStart =
        prices[`plan_start_date_${periodId}`] ??
        (prices.plan_start_dates && prices.plan_start_dates[periodId]) ??
        null;

      const mkSide = (prefix) => {
        const amt = prices[`amount_for_${prefix}_${periodId}`];
        if (amt == null || amt === '') return null;
        const reg = prices[`registration_fee_${prefix}_${periodId}`];
        const numInst = prices[`number_of_installments_${prefix}_${periodId}`];
        const instArr = prices[`installment_amounts_${prefix}_${periodId}`];
        return {
          total: parseFloat(amt),
          registrationFee: reg != null && reg !== '' ? parseFloat(reg) : null,
          numInstallments:
            numInst != null && numInst !== '' ? parseInt(numInst, 10) : null,
          installmentAmounts: Array.isArray(instArr) ? instArr.map((x) => parseFloat(x)) : null,
        };
      };

      return {
        periodId,
        planStartDate: planStart,
        online: mkSide('online'),
        inPerson: mkSide('in_person'),
      };
    })
    .filter((g) => g.online || g.inPerson);
}

function normalizeFullYearInstallment(fy) {
  if (!fy) return null;
  const raw = fy.installment_items ?? fy.installmentItems ?? [];
  const items = Array.isArray(raw) ? [...raw] : [];
  items.sort((a, b) => (a.installment_number || 0) - (b.installment_number || 0));
  return {
    total_course_fee: fy.total_course_fee != null ? parseFloat(fy.total_course_fee) : null,
    number_of_installments: fy.number_of_installments != null ? parseInt(fy.number_of_installments, 10) : null,
    installment_items: items.map((it) => ({
      installment_number: it.installment_number,
      amount: it.amount != null ? parseFloat(it.amount) : null,
    })),
  };
}

function formatMoney(amount, currencySym) {
  if (amount == null || Number.isNaN(amount)) return '—';
  let sym = currencySym || '£';
  if (typeof sym === 'string') {
    const c = sym.toLowerCase();
    if (c === 'gbp') sym = '£';
    else if (c === 'eur') sym = '€';
    else if (c === 'usd') sym = '$';
  }
  return `${sym}${amount.toFixed(2)}`;
}

/**
 * Depth-first flatten of the nested category tree into select options, each labelled with its
 * full path ("Revision > Topic Revision > Maths") so sibling names stay unambiguous.
 */
function flattenCategories(nodes, prefix = '', out = []) {
  (nodes || []).forEach((node) => {
    const label = prefix ? `${prefix} > ${node.name}` : node.name;
    out.push({ id: node.id, label });
    flattenCategories(node.all_children || node.children || [], label, out);
  });
  return out;
}

const CourseList = () => {
  const navigate = useNavigate();
  const canCreateCourses = adminHasPermission('create-courses');
  const canUpdateCourses = adminHasPermission('update-courses');
  const canDeleteCourses = adminHasPermission('delete-courses');

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [totalCourses, setTotalCourses] = useState(0);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseDetails, setCourseDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, course: null });
  const [deletingCourseId, setDeletingCourseId] = useState(null);
  const [togglingCourseId, setTogglingCourseId] = useState(null);

  const fetchCourses = async (pageNumber, search = '', categoryId = '') => {
    setLoading(true);
    setError(null);
    try {
      let url = `admin/assign/course?page=${pageNumber + 1}`;
      if (search.trim()) {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }
      if (categoryId) {
        url += `&category_id=${encodeURIComponent(categoryId)}`;
      }
      const res = await api.get(url);
      const coursesData = res.data?.data?.data || [];
      setCourses(coursesData);
      setTotalCourses(res.data?.data?.total || 0);
    } catch (err) {
      // Always show generic error message - never expose backend/database errors to users
      setError('Failed to fetch courses, please try again.');
      setCourses([]);
      setTotalCourses(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses(page, searchQuery, categoryFilter);
  }, [page, searchQuery, categoryFilter]);

  useEffect(() => {
    api
      .get('admin/mock-exam/category-tree')
      .then((res) => setCategoryOptions(flattenCategories(res.data?.data || [])))
      .catch(() => setCategoryOptions([]));
  }, []);

  // Fetch full course details when View (details dialog) is opened
  useEffect(() => {
    if (!selectedCourse?.id) {
      setCourseDetails(null);
      return;
    }
    let cancelled = false;
    setLoadingDetails(true);
    setCourseDetails(null);
    api.get(`admin/assign/course/${selectedCourse.id}`)
      .then((res) => {
        if (!cancelled && res.data?.success && res.data?.data) {
          setCourseDetails(res.data.data);
        }
      })
      .catch(() => {
        if (!cancelled) setCourseDetails(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingDetails(false);
      });
    return () => { cancelled = true; };
  }, [selectedCourse?.id]);

  const handleChangePage = (_, newPage) => setPage(newPage);

  /** Prefer GET course/:id payload once loaded (even if empty); do not keep stale list row when details return []. */
  const locationsForDetailsDialog = useMemo(() => {
    if (courseDetails != null && Array.isArray(courseDetails.locations)) {
      return courseDetails.locations;
    }
    return selectedCourse?.locations ?? [];
  }, [courseDetails, selectedCourse?.locations]);

  /** From GET course/:id — per-subject weekday, held_time & duration_minutes (course_subject pivot), not venue slots. */
  const subjectScheduleRows = useMemo(() => {
    const subs = courseDetails?.subjects;
    if (!Array.isArray(subs) || subs.length === 0) return [];
    return subs.map((s) => {
      if (typeof s === 'string') {
        return { name: s, held_weekday_name: null, held_time: null, duration_minutes: null };
      }
      return {
        name: s.name ?? '—',
        held_weekday_name: s.held_weekday_name ?? null,
        held_time: s.held_time ?? null,
        duration_minutes: s.duration_minutes ?? null,
      };
    });
  }, [courseDetails?.subjects]);

  const hasCourseRunDates = Boolean(
    courseDetails && (courseDetails.start_date || courseDetails.end_date)
  );

  const pricingPlanGroups = useMemo(
    () => buildPricingPlanGroups(courseDetails?.prices),
    [courseDetails?.prices]
  );

  const fullYearPlan = useMemo(
    () => normalizeFullYearInstallment(courseDetails?.full_year_installment),
    [courseDetails?.full_year_installment]
  );

  const defaultCurrency =
    courseDetails?.price_plans?.[0]?.currency ||
    courseDetails?.full_year_currency ||
    '£';

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
    setPage(0); // Reset to first page when searching
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setPage(0);
  };

  // Helper function to check if course is active
  const isCourseActive = (course) => {
    if (!course) return false;
    
    // Extract status - API returns "status": 2 for active
    const statusValue = course.status || course.status_id || course.course_status || course.state;
    
    if (statusValue === undefined || statusValue === null) {
      return false; // Default to inactive if status not found
    }
    
    // Handle string values
    if (typeof statusValue === 'string') {
      const lowerStatus = statusValue.toLowerCase();
      if (lowerStatus === 'active' || lowerStatus === '1' || lowerStatus === '2') {
        return true;
      }
      return false;
    }
    
    // Handle number values - API uses 2 for active, but support 1 as well
    const statusNum = typeof statusValue === 'string' ? parseInt(statusValue, 10) : statusValue;
    
    // Status 1 or 2 = Active, Status 0 or 3 = Inactive
    return statusNum === 1 || statusNum === 2;
  };

  const getCourseId = (course) => {
    if (!course) return null;
    const courseId = course.id ?? course.course_id;
    if (courseId === undefined || courseId === null || courseId === '') return null;
    return courseId;
  };

  const handleEdit = (course) => {
    const courseId = getCourseId(course);
    if (!courseId) {
      toast.error('Cannot edit: Course ID not found');
      return;
    }
    navigate(`/admin/course/${courseId}`);
  };

  const handleDeleteClick = (course) => {
    setDeleteDialog({ open: true, course });
  };

  const handleDeleteConfirm = async () => {
    const course = deleteDialog.course;
    if (!course) return;

    const courseId = getCourseId(course);
    if (!courseId) {
      toast.error('Cannot delete: Course ID not found');
      setDeleteDialog({ open: false, course: null });
      return;
    }

    setDeletingCourseId(courseId);

    try {
      const response = await api.delete(`admin/assign/course/${courseId}`);
      
      // Helper function to validate message (not just a status code)
      const getValidMessage = (message, defaultMsg) => {
        if (!message || typeof message !== 'string') return defaultMsg;
        const trimmed = message.trim();
        // Check if message is just a number (like "200") or empty
        if (trimmed === '' || /^\d+$/.test(trimmed)) return defaultMsg;
        return trimmed;
      };
      
      if (response.data.success || response.status === 200) {
        const message = getValidMessage(response.data.message, 'Course deleted successfully');
        toast.success(message);
        setDeleteDialog({ open: false, course: null });
        fetchCourses(page, searchQuery, categoryFilter);
      } else {
        const errorMessage = getValidMessage(response.data.message, 'Failed to delete course');
        toast.error(errorMessage);
      }
    } catch (err) {
      if (err.response?.status === 422) {
        toast.error(err.response.data.error || 'Cannot delete course. One or more students are assigned to this course.');
      } else if (err.response?.status === 404) {
        toast.error('Course not found');
        fetchCourses(page, searchQuery, categoryFilter);
      } else if (err.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        toast.error(err.response?.data?.error || 'Failed to delete course');
      }
    } finally {
      setDeletingCourseId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, course: null });
  };

  const handleToggleStatus = async (course) => {
    const courseId = getCourseId(course);
    if (!courseId) {
      toast.error('Cannot toggle status: Course ID not found');
      return;
    }

    // Extract status value
    const isActive = isCourseActive(course);
    
    // If currently active, we want to deactivate. If currently inactive, we want to activate.
    const action = isActive ? 'deactivate' : 'activate';
    
    setTogglingCourseId(courseId);
    
    try {
      const response = await api.patch(`admin/assign/course/${courseId}/toggle-status`, {
        action: action
      });
      
      if (response.data.success) {
        toast.success(response.data.message || `Course ${action}d successfully`);
        fetchCourses(page, searchQuery, categoryFilter);
      } else {
        toast.error(response.data.message || `Failed to ${action} course`);
      }
    } catch (err) {
      if (err.response?.status === 422) {
        const errors = err.response.data.errors;
        if (errors?.action) {
          toast.error(errors.action[0]);
        } else {
          toast.error(err.response.data.message || 'Invalid action');
        }
      } else if (err.response?.status === 404) {
        toast.error('Course not found');
        fetchCourses(page, searchQuery, categoryFilter);
      } else if (err.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        toast.error(err.response?.data?.error || 'Failed to toggle course status');
      }
    } finally {
      setTogglingCourseId(null);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      p: 3 
    }}>
      {/* Compact Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        {/* Left Side - Title & Description */}
        <Box sx={{ textAlign: 'left', flex: 1, mr: 3 }}>
          <Typography component="h1" sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 700, color: '#111827', lineHeight: 1.3, mb: 1 }}>
            Course List
          </Typography>
          <Box sx={{ maxWidth: 350 }}>
            <Typography variant="body2" component="div" sx={{ color: '#5a6c7d', fontSize: '13px' }}>
              Manage all courses with comprehensive details, search and filtering options
            </Typography>
            <Typography variant="body1" component="div" sx={{ opacity: 0.9, mt: 1.5, color: '#5a6c7d', fontSize: '13px' }}>
              Total courses - {totalCourses} {searchQuery ? `| Courses found for "${searchQuery}"` : ''}
            </Typography>
          </Box>
        </Box>

        {/* Right Side - Add Course Button */}
        {canCreateCourses ? (
          <Button sx={gradientButtonStyle} onClick={() => navigate('/admin/course')}>
            + Add Course
          </Button>
        ) : null}
      </Box>


      {/* Search Field */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search courses by name, academic year, subjects, or any other field..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear search"
                  onClick={handleClearSearch}
                  edge="end"
                  size="small"
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              borderRadius: 2,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: 'white',
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
                borderWidth: 2,
              },
            },
          }}
        />
        <TextField
          select
          size="small"
          label="Product category"
          value={categoryFilter}
          onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}
          sx={{ mt: 2, minWidth: 280, backgroundColor: 'white', borderRadius: 2 }}
          helperText="Selecting a parent also includes everything beneath it"
        >
          <MenuItem value="">All categories</MenuItem>
          {categoryOptions.map((c) => (
            <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
          ))}
        </TextField>
      </Box>


      {/* Courses Table Card */}
      {/* Full width: a 1200px cap cut off Status / Actions; the table scrolls horizontally inside when needed. */}
      <Card sx={{
        width: '100%',
        maxWidth: '100%',
        mx: 'auto',
        borderRadius: 4,
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(20px)'
      }}>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <TableContainer sx={courseTableScrollSx}>
              <Table stickyHeader>
                <TableHead
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '& .MuiTableCell-head': {
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    },
                  }}
                >
                  <TableRow>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}><Skeleton width={120} /></TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}><Skeleton width={160} /></TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}><Skeleton width={140} /></TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}><Skeleton width={200} /></TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}><Skeleton width={150} /></TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}><Skeleton width={120} /></TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}><Skeleton width={100} /></TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}><Skeleton width={80} /></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: 10 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell><Skeleton width="80%" /></TableCell>
                      <TableCell><Skeleton width="60%" /></TableCell>
                      <TableCell><Skeleton width="90%" /></TableCell>
                      <TableCell><Skeleton width="70%" /></TableCell>
                      <TableCell><Skeleton width="60%" /></TableCell>
                      <TableCell><Skeleton width="50%" /></TableCell>
                      <TableCell><Skeleton width="40%" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <>
              <TableContainer sx={courseTableScrollSx}>
                <Table stickyHeader>
                  <TableHead
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      '& .MuiTableCell-head': {
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 120 }}>Academic Year</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 220 }}>Course Name</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 150 }}>Category</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 150 }}>Subjects</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 120 }}>Locations</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 120 }}>Modes</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 150 }}>Pricing</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 120 }}>Seats</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 100 }}>Status</TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {error ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <Typography sx={{ fontSize: '48px' }}>⚠️</Typography>
                            <Typography variant="h6" sx={{ color: '#d32f2f', fontWeight: 600 }}>
                              Error Loading Courses
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666', maxWidth: 400, textAlign: 'center' }}>
                              {error}
                            </Typography>
                            <Button 
                              sx={gradientButtonStyle} 
                              onClick={() => fetchCourses(page, searchQuery)}
                            >
                              🔄 Retry
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : courses.length > 0 ? (
                      courses.map((course, index) => (
                        <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.05)' } }}>
                          <TableCell sx={{ fontWeight: 500 }}>{course.acdemicyear || '—'}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {course.image ? (
                                <Avatar src={course.image} sx={{ width: 32, height: 32 }} />
                              ) : (
                                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                                  📘
                                </Avatar>
                              )}
                              <Typography variant="body2" fontWeight={600}>
                                {course.name || '—'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {course.category_name ? (
                              <Chip
                                label={course.category_name}
                                size="small"
                                sx={{ background: 'linear-gradient(45deg, #0ea5e9, #6366f1)', color: 'white', fontWeight: 600 }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary">Uncategorised</Typography>
                            )}
                            {course.course_format === 'short' && (
                              <Chip
                                label="Short"
                                size="small"
                                color="warning"
                                title="Short course: one-time payment, Day N sessions"
                                sx={{ ml: 0.5 }}
                              />
                            )}
                            {course.origin === 'auto_weak_area' && (
                              <Chip
                                label="Auto"
                                size="small"
                                variant="outlined"
                                title="Proposed from a weak-topic cohort"
                                sx={{ ml: 0.5 }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 200 }}>
                              {course.subjects?.length ? (
                                course.subjects.slice(0, 2).map((sub, i) => (
                                  <Chip key={i} label={sub} size="small" sx={{ background: 'linear-gradient(45deg, #667eea, #764ba2)', color: 'white', fontWeight: 600 }} />
                                ))
                              ) : (
                                <Typography variant="caption" color="text.secondary">No subjects</Typography>
                              )}
                              {course.subjects?.length > 2 && (
                                <Chip label={`+${course.subjects.length - 2} more`} size="small" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 150 }}>
                              {course.locations?.length ? (
                                course.locations.slice(0, 2).map((loc, i) => (
                                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <LocationOnIcon fontSize="small" color="action" />
                                    <Chip label={loc.name} size="small" sx={{ background: 'linear-gradient(45deg, #ff9800, #f57c00)', color: 'white', fontWeight: 600 }} />
                                  </Box>
                                ))
                              ) : (
                                <Typography variant="caption" color="text.secondary">No locations</Typography>
                              )}
                              {course.locations?.length > 2 && (
                                <Chip label={`+${course.locations.length - 2} more`} size="small" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {course.modes?.length ? (
                                course.modes.map((mode, i) => (
                                  <Chip 
                                    key={i} 
                                    label={mode} 
                                    size="small" 
                                    sx={{
                                      background: mode === 'Online' 
                                        ? 'linear-gradient(45deg, #4caf50, #2e7d32)' 
                                        : 'linear-gradient(45deg, #2196f3, #1565c0)',
                                      color: 'white',
                                      fontWeight: 600
                                    }}
                                  />
                                ))
                              ) : (
                                <Typography variant="caption" color="text.secondary">No modes</Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              {course.amounts && Object.keys(course.amounts).length > 0 ? (
                                <Box>
                                  {Object.entries(course.amounts).slice(0, 2).map(([period, price], i) => (
                                    <Typography key={i} variant="caption" display="block" fontWeight={600}>
                                      {period}: {price}
                                    </Typography>
                                  ))}
                                  {Object.keys(course.amounts).length > 2 && (
                                    <Typography variant="caption" color="text.secondary">
                                      +{Object.keys(course.amounts).length - 2} more
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.secondary">No pricing</Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700}>
                              {course.remaining_seats ?? '—'} / {course.total_seats ?? '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={isCourseActive(course) ? 'Active' : 'Inactive'}
                              size="small"
                              color={isCourseActive(course) ? 'success' : 'default'}
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <RowActions
                              actions={[
                                { label: 'View details', icon: <VisibilityIcon fontSize="small" />, onClick: () => setSelectedCourse(course) },
                                { label: 'Open course page', icon: <EditIcon fontSize="small" />, onClick: () => handleEdit(course), show: canUpdateCourses },
                                { label: isCourseActive(course) ? 'Deactivate' : 'Activate', icon: isCourseActive(course) ? <ToggleOffIcon /> : <ToggleOnIcon />, onClick: () => handleToggleStatus(course), loading: togglingCourseId === getCourseId(course), show: canUpdateCourses },
                                { label: 'Delete course', icon: <DeleteIcon />, onClick: () => handleDeleteClick(course), loading: deletingCourseId === getCourseId(course), show: canDeleteCourses, danger: true },
                              ]}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                          <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>No Courses Found</Typography>
                          <Typography variant="body2" sx={{ color: '#999' }}>No courses match your current search criteria</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {courses.length > 0 && (
                <TablePagination
                  component="div"
                  count={totalCourses}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[10]}
                  sx={{ borderTop: '1px solid rgba(0,0,0,0.1)' }}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog 
        open={!!selectedCourse} 
        onClose={() => setSelectedCourse(null)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.2)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderRadius: '16px 16px 0 0',
          p: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '50%',
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Typography sx={{ fontSize: '28px' }}>📚</Typography>
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                Course Details
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Complete course information and specifications
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              onClick={() => setSelectedCourse(null)}
              sx={{ 
                color: 'white',
                background: 'rgba(255,255,255,0.1)',
                '&:hover': { 
                  background: 'rgba(255,255,255,0.2)',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {selectedCourse && (
            <Box sx={{ p: 4, background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)' }}>
              <Grid container spacing={4}>
                {/* Basic Information Card */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ 
                    borderRadius: 3,
                    background: 'rgba(255,255,255,0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(102, 126, 234, 0.1)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    p: 3,
                    height: 'fit-content'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                      <Box sx={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '50%',
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Typography sx={{ fontSize: '16px' }}>ℹ️</Typography>
                      </Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#2d3748' }}>
                        Basic Information
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#4a5568', minWidth: 120 }}>
                          Academic Year:
                        </Typography>
                        <Chip 
                          label={selectedCourse.acdemicyear || '—'} 
                          size="small" 
                          sx={{ background: 'linear-gradient(45deg, #667eea, #764ba2)', color: 'white', fontWeight: 600 }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#4a5568', minWidth: 120 }}>
                          Course Name:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#2d3748' }}>
                          {selectedCourse.name || '—'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#4a5568', minWidth: 120 }}>
                          Slug:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#2d3748', fontFamily: 'monospace', background: '#f7fafc', px: 1, py: 0.5, borderRadius: 1 }}>
                          {selectedCourse.slug || '—'}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
                
                {/* Course Image Card */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ 
                    borderRadius: 3,
                    background: 'rgba(255,255,255,0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(102, 126, 234, 0.1)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    p: 3,
                    height: 'fit-content'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                      <Box sx={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '50%',
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Typography sx={{ fontSize: '16px' }}>🖼️</Typography>
                      </Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#2d3748' }}>
                        Course Image
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      width: '100%', 
                      height: 200, 
                      overflow: 'hidden', 
                      borderRadius: 3, 
                      border: '2px solid rgba(102, 126, 234, 0.1)',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      position: 'relative'
                    }}>
                      {selectedCourse.image ? (
                        <img
                          src={selectedCourse.image}
                          alt={selectedCourse.name || 'Course'}
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover',
                            transition: 'transform 0.3s ease'
                          }}
                          onError={(e) => { e.target.src = '/no-image.png'; }}
                        />
                      ) : (
                        <Box sx={{ 
                          width: '100%', 
                          height: '100%', 
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'center', 
                          justifyContent: 'center',
                          background: 'linear-gradient(145deg, #f7fafc 0%, #edf2f7 100%)',
                          color: '#718096'
                        }}>
                          <Typography sx={{ fontSize: '48px', mb: 1 }}>📚</Typography>
                          <Typography variant="body2" fontWeight={500}>No Image Available</Typography>
                        </Box>
                      )}
                    </Box>
                  </Card>
                </Grid>

                {/* Subjects Card */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ 
                    borderRadius: 3,
                    background: 'rgba(255,255,255,0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(102, 126, 234, 0.1)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    p: 3,
                    height: 'fit-content'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                      <Box sx={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '50%',
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Typography sx={{ fontSize: '16px' }}>📖</Typography>
                      </Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#2d3748' }}>
                        Subjects ({selectedCourse.subjects?.length || 0})
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                      {selectedCourse.subjects?.length ? (
                        selectedCourse.subjects.map((sub, i) => (
                          <Chip 
                            key={i} 
                            label={sub} 
                            size="medium" 
                            sx={{ 
                              background: 'linear-gradient(45deg, #667eea, #764ba2)', 
                              color: 'white', 
                              fontWeight: 600,
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                              },
                              transition: 'all 0.3s ease'
                            }}
                          />
                        ))
                      ) : (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1, 
                          color: '#718096',
                          fontStyle: 'italic'
                        }}>
                          <Typography sx={{ fontSize: '20px' }}>📝</Typography>
                          <Typography variant="body2">No subjects assigned</Typography>
                        </Box>
                      )}
                    </Box>
                  </Card>
                </Grid>

                {/* Modes Card */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ 
                    borderRadius: 3,
                    background: 'rgba(255,255,255,0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(102, 126, 234, 0.1)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    p: 3,
                    height: 'fit-content'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                      <Box sx={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '50%',
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Typography sx={{ fontSize: '16px' }}>🎯</Typography>
                      </Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#2d3748' }}>
                        Course Modes
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                      {selectedCourse.modes?.length ? (
                        selectedCourse.modes.map((mode, i) => (
                          <Chip 
                            key={i} 
                            label={mode} 
                            size="medium" 
                            sx={{
                              background: mode === 'Online' 
                                ? 'linear-gradient(45deg, #4caf50, #2e7d32)' 
                                : 'linear-gradient(45deg, #2196f3, #1565c0)',
                              color: 'white',
                              fontWeight: 600,
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                              },
                              transition: 'all 0.3s ease'
                            }}
                          />
                        ))
                      ) : (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1, 
                          color: '#718096',
                          fontStyle: 'italic'
                        }}>
                          <Typography sx={{ fontSize: '20px' }}>🚫</Typography>
                          <Typography variant="body2">No modes specified</Typography>
                        </Box>
                      )}
                    </Box>
                  </Card>
                </Grid>

                {/* Pricing & Installments Card */}
                <Grid item xs={12}>
                  <Card sx={{ 
                    borderRadius: 3,
                    background: 'rgba(255,255,255,0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(102, 126, 234, 0.1)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    p: 3
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                      <Box sx={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '50%',
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Typography sx={{ fontSize: '16px' }}>💰</Typography>
                      </Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#2d3748' }}>
                        Pricing & Installments
                      </Typography>
                    </Box>
                    {loadingDetails ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3, justifyContent: 'center' }}>
                        <CircularProgress size={28} />
                        <Typography variant="body2" color="text.secondary">Loading pricing details…</Typography>
                      </Box>
                    ) : (
                      <Stack spacing={3}>
                        {pricingPlanGroups.length > 0 && (
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                              Subscription plans (by billing period — months)
                            </Typography>
                            <Grid container spacing={2}>
                              {pricingPlanGroups.map((g) => (
                                <Grid item xs={12} sm={6} key={g.periodId}>
                                <Paper
                                  variant="outlined"
                                  sx={{ p: 2, borderRadius: 2, borderColor: 'rgba(102, 126, 234, 0.35)', height: '100%' }}
                                >
                                  <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#2d3748', mb: 0.5 }}>
                                    {g.periodId}-month plan
                                  </Typography>
                                  {g.planStartDate && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                      Plan start date: <strong>{g.planStartDate}</strong>
                                    </Typography>
                                  )}
                                  <Grid container spacing={2}>
                                    {g.online && (
                                      <Grid item xs={12} md={g.inPerson ? 6 : 12}>
                                        <Box
                                          sx={{
                                            p: 2,
                                            bgcolor: 'rgba(76, 175, 80, 0.06)',
                                            borderRadius: 2,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            height: '100%',
                                          }}
                                        >
                                          <Typography variant="subtitle2" fontWeight={700} color="success.dark" gutterBottom>
                                            Online
                                          </Typography>
                                          <Typography variant="body2">
                                            Course fee: <strong>{formatMoney(g.online.total, defaultCurrency)}</strong>
                                          </Typography>
                                          <Typography variant="body2">
                                            Registration: {formatMoney(g.online.registrationFee, defaultCurrency)}
                                          </Typography>
                                          <Typography variant="body2">
                                            No. of installments: {g.online.numInstallments ?? '—'}
                                          </Typography>
                                          {g.online.installmentAmounts?.length ? (
                                            <Box sx={{ mt: 1 }}>
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                Installment breakdown
                                              </Typography>
                                              {g.online.installmentAmounts.map((amt, i) => (
                                                <Typography key={i} variant="body2" sx={{ pl: 1 }}>
                                                  #{i + 1}: {formatMoney(amt, defaultCurrency)}
                                                </Typography>
                                              ))}
                                            </Box>
                                          ) : g.online.numInstallments > 0 && g.online.total != null ? (
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                              Avg. per installment:{' '}
                                              {formatMoney(g.online.total / g.online.numInstallments, defaultCurrency)}
                                            </Typography>
                                          ) : null}
                                        </Box>
                                      </Grid>
                                    )}
                                    {g.inPerson && (
                                      <Grid item xs={12} md={g.online ? 6 : 12}>
                                        <Box
                                          sx={{
                                            p: 2,
                                            bgcolor: 'rgba(33, 150, 243, 0.06)',
                                            borderRadius: 2,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            height: '100%',
                                          }}
                                        >
                                          <Typography variant="subtitle2" fontWeight={700} color="primary.dark" gutterBottom>
                                            In-person
                                          </Typography>
                                          <Typography variant="body2">
                                            Course fee: <strong>{formatMoney(g.inPerson.total, defaultCurrency)}</strong>
                                          </Typography>
                                          <Typography variant="body2">
                                            Registration: {formatMoney(g.inPerson.registrationFee, defaultCurrency)}
                                          </Typography>
                                          <Typography variant="body2">
                                            No. of installments: {g.inPerson.numInstallments ?? '—'}
                                          </Typography>
                                          {g.inPerson.installmentAmounts?.length ? (
                                            <Box sx={{ mt: 1 }}>
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                Installment breakdown
                                              </Typography>
                                              {g.inPerson.installmentAmounts.map((amt, i) => (
                                                <Typography key={i} variant="body2" sx={{ pl: 1 }}>
                                                  #{i + 1}: {formatMoney(amt, defaultCurrency)}
                                                </Typography>
                                              ))}
                                            </Box>
                                          ) : g.inPerson.numInstallments > 0 && g.inPerson.total != null ? (
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                              Avg. per installment:{' '}
                                              {formatMoney(g.inPerson.total / g.inPerson.numInstallments, defaultCurrency)}
                                            </Typography>
                                          ) : null}
                                        </Box>
                                      </Grid>
                                    )}
                                  </Grid>
                                </Paper>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        )}

                        {(fullYearPlan ||
                          (courseDetails?.full_year_registration_fee != null &&
                            courseDetails.full_year_registration_fee !== '')) && (
                          <Box>
                            <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#2d3748', mb: 1 }}>
                              Full academic year (53 weeks)
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                              <Typography variant="body2" sx={{ mb: fullYearPlan ? 1 : 0 }}>
                                Registration fee:{' '}
                                <strong>
                                  {formatMoney(
                                    parseFloat(String(courseDetails.full_year_registration_fee)),
                                    defaultCurrency
                                  )}
                                </strong>
                              </Typography>
                              {fullYearPlan && (
                                <>
                                  <Typography variant="body2">
                                    Total course fee: {formatMoney(fullYearPlan.total_course_fee, defaultCurrency)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    Installments: {fullYearPlan.number_of_installments ?? '—'}
                                  </Typography>
                                  {fullYearPlan.installment_items?.length > 0 && (
                                    <>
                                      <Typography variant="caption" color="text.secondary">
                                        Installment breakdown
                                      </Typography>
                                      {fullYearPlan.installment_items.map((it, idx) => (
                                        <Typography key={idx} variant="body2" sx={{ pl: 1 }}>
                                          #{it.installment_number ?? idx + 1}: {formatMoney(it.amount, defaultCurrency)}
                                        </Typography>
                                      ))}
                                    </>
                                  )}
                                </>
                              )}
                            </Paper>
                          </Box>
                        )}

                        {!loadingDetails &&
                          pricingPlanGroups.length === 0 &&
                          !fullYearPlan &&
                          (courseDetails?.full_year_registration_fee == null ||
                            courseDetails.full_year_registration_fee === '') && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                color: '#718096',
                                fontStyle: 'italic',
                                justifyContent: 'center',
                                py: 3,
                              }}
                            >
                              <Typography sx={{ fontSize: '24px' }}>💸</Typography>
                              <Typography variant="body2">No pricing information available</Typography>
                            </Box>
                          )}
                      </Stack>
                    )}
                  </Card>
                </Grid>

                {/* Locations and Slots Card */}
                <Grid item xs={12}>
                  <Card sx={{ 
                    borderRadius: 3,
                    background: 'rgba(255,255,255,0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(102, 126, 234, 0.1)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    p: 3
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                      <Box sx={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '50%',
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Typography sx={{ fontSize: '16px' }}>📍</Typography>
                      </Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#2d3748' }}>
                        Schedule, locations &amp; time slots
                      </Typography>
                    </Box>
                    {loadingDetails ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3, justifyContent: 'center' }}>
                        <CircularProgress size={28} />
                        <Typography variant="body2" color="text.secondary">Loading schedule &amp; venues…</Typography>
                      </Box>
                    ) : (
                      <>
                        {hasCourseRunDates && (
                          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              Course run dates
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {(courseDetails.start_date || '—')} → {(courseDetails.end_date || '—')}
                            </Typography>
                          </Box>
                        )}

                        {subjectScheduleRows.length > 0 && (
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                              Per-subject schedule
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                              Start time and duration saved for each subject. This is separate from venue/class slots below.
                            </Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                                    <TableCell><strong>Subject</strong></TableCell>
                                    <TableCell><strong>Weekday</strong></TableCell>
                                    <TableCell><strong>Start time</strong></TableCell>
                                    <TableCell><strong>Duration</strong></TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {subjectScheduleRows.map((row, idx) => (
                                    <TableRow key={idx} hover>
                                      <TableCell>{row.name}</TableCell>
                                      <TableCell>
                                        {row.held_weekday_name != null && row.held_weekday_name !== '' ? row.held_weekday_name : '—'}
                                      </TableCell>
                                      <TableCell>
                                        {row.held_time != null && row.held_time !== '' ? row.held_time : '—'}
                                      </TableCell>
                                      <TableCell>
                                        {row.duration_minutes != null && row.duration_minutes !== ''
                                          ? `${row.duration_minutes} min`
                                          : '—'}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}

                        {(subjectScheduleRows.length > 0 || hasCourseRunDates) && (
                          <Divider sx={{ my: 2 }} />
                        )}

                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                          Venues &amp; class time slots
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                          Optional in-person class slots linked to locations (managed under Course time slots).
                        </Typography>

                        {locationsForDetailsDialog?.length ? (
                    <Grid container spacing={2}>
                      {locationsForDetailsDialog.map((location, i) => (
                        <Grid item xs={12} md={6} key={i}>
                          <Card sx={{ border: '1px solid #e0e0e0' }}>
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <LocationOnIcon color="secondary" />
                                <Typography variant="h6" fontWeight={600}>
                                  {location.name}
                                </Typography>
                              </Box>
                              
                              {location.slots?.length > 0 ? (
                                <Box>
                                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                                    Available Slots ({location.slots.length}):
                                  </Typography>
                                  {location.slots.map((slot, slotIndex) => (
                                    <Card key={slotIndex} sx={{ mb: 1, bgcolor: 'grey.50' }}>
                                      <CardContent sx={{ py: 1.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                          <Chip label={slot.class} size="small" color="primary" />
                                          <Chip label={slot.weekday} size="small" color="secondary" />
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <AccessTimeIcon fontSize="small" />
                                            <Typography variant="caption">
                                              {slot.start_end_time}
                                            </Typography>
                                          </Box>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <PeopleIcon fontSize="small" />
                                            <Typography variant="caption">
                                              {slot.seat_left}/{slot.max_seats} seats
                                            </Typography>
                                          </Box>
                                        </Box>
                                      </CardContent>
                                    </Card>
                                  ))}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No time slots available
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                      </Grid>
                    ) : (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1, 
                        color: '#718096',
                        fontStyle: 'italic',
                        justifyContent: 'center',
                        py: 2
                      }}>
                        <Typography sx={{ fontSize: '24px' }}>🏢</Typography>
                        <Typography variant="body2">No venue/class slots configured for this course.</Typography>
                      </Box>
                    )}

                        {!loadingDetails && !hasCourseRunDates && subjectScheduleRows.length === 0 && !locationsForDetailsDialog?.length && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                            No course dates, subject schedule, or venue slots to display. Open the course in edit to add them.
                          </Typography>
                        )}
                      </>
                    )}
                  </Card>
                </Grid>

                {/* Features and Description */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: 'primary.main' }}>
                    Features
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {selectedCourse.features || 'No features description available'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: 'primary.main' }}>
                    Description
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {selectedCourse.description || 'No description available'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          pt: 2,
          background: 'linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)',
          borderTop: '1px solid rgba(0,0,0,0.1)',
          borderRadius: '0 0 16px 16px'
        }}>
          <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => setSelectedCourse(null)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                px: 3
              }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                if (!selectedCourse) return;
                const courseId = getCourseId(selectedCourse);
                if (!courseId) {
                  toast.error('Cannot open course: ID not found');
                  return;
                }
                setSelectedCourse(null);
                navigate(`/admin/course/${courseId}`);
              }}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                px: 4,
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a42a0 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.3)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Open course page
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5,
          color: 'error.main',
          pb: 1
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: 'error.light',
            color: 'error.main',
            fontSize: '20px'
          }}>
            ⚠️
          </Box>
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            Are you sure you want to delete this course? This action cannot be undone.
          </Typography>
          {deleteDialog.course && (
            <Box sx={{ 
              p: 2, 
              backgroundColor: 'grey.50', 
              borderRadius: 1, 
              border: '1px solid', 
              borderColor: 'grey.300' 
            }}>
              <Typography variant="subtitle1" fontWeight={600}>
                <strong>Course:</strong> {deleteDialog.course.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Academic Year:</strong> {deleteDialog.course.acdemicyear || 'N/A'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, gap: 1.5 }}>
          <Button 
            onClick={handleDeleteCancel}
            variant="outlined"
            color="inherit"
            size="large"
            sx={{ 
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            size="large"
            disabled={deletingCourseId === deleteDialog.course?.id}
            autoFocus
            sx={{ 
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
          >
            {deletingCourseId === deleteDialog.course?.id ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Deleting...
              </Box>
            ) : (
              'Delete Course'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CourseList;
